import React from 'react'

import {
  Accordion,
  AlignItems,
  Body2,
  Bold,
  BorderStyle,
  Box,
  Color,
  Display,
  FontFamily,
  Heading2,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Link,
  LinkVariant,
  Space,
  Text,
} from '@gousto-internal/citrus-react'
import { LandingPageButton } from '../../components'
import { HowGoustoFoodBoxesWorkTypes } from '../../types'
import {
  HowGoustoFoodBoxesWorkSections,
  TargetUri,
  TrackingActions,
  simpleTrackRedirect,
} from '../../types/tracking'

const renderAccordionHeader = (question: string) => {
  const AccordionHeader = (isExpanded: boolean) => (
    <Box
      display={Display.Flex}
      justifyContent={JustifyContent.SpaceBetween}
      alignItems={AlignItems.Center}
    >
      <Heading3 color={Color.White} fontFamily={FontFamily.Bold} size={6}>
        {question}
      </Heading3>

      <Text color={Color.White} size={15}>
        <Icon
          variant={IconVariant.Inherit}
          name={isExpanded ? 'chevron_large_up' : 'chevron_large_down'}
        />
      </Text>
    </Box>
  )

  return AccordionHeader
}

export const FAQSection = ({ startOnboarding }: HowGoustoFoodBoxesWorkTypes) => (
  <Box paddingH={[8, 20]} paddingV={16} display={Display.Block} bg={Color.ColdGrey_600}>
    <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
      <Box maxWidth="1033px" width="100%">
        <Box paddingBottom={2}>
          <Heading2 color={Color.White}>Gousto food boxes FAQs</Heading2>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="0" title={renderAccordionHeader('Are Gousto portions generous?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Absolutely. Our recipes are perfectly portioned to satisfy your appetite. Feeling
                extra hungry? You can always increase the number of portions to get to super-size
                your meal. Find out more on our{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={HowGoustoFoodBoxesWorkSections.FAQ_SECTION}
                  target={TargetUri.BOX_PRICES}
                  action={TrackingActions.BOX_PRICING_NAVIGATION_CLICKED}
                  whiteText
                >
                  <Bold>box prices page.</Bold>
                </LandingPageButton>{' '}
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="1"
            title={renderAccordionHeader('Do I have to have a recipe box delivered every week?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Nope. Our food box subscription is super flexible. You can pause, skip or stop
                deliveries anytime from the app or website. Choose from weekly, fornightly, or even
                a monthly subscription to suit your household needs. You&apos;re totally in charge.
                No awkward break-up needed.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="2" title={renderAccordionHeader('Are Gousto food boxes sustainable?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Gousto is very sustainable. In fact, we&apos;re a{' '}
                <Link
                  variant={LinkVariant.None}
                  style={{ color: 'white', textDecorationLine: 'underline' }}
                  href={TargetUri.SUSTAINABILITY}
                  onClick={() =>
                    simpleTrackRedirect(
                      TrackingActions.SUSTAINABILITY_NAVIGATION_CLICKED,
                      HowGoustoFoodBoxesWorkSections.FAQ_SECTION,
                      TargetUri.SUSTAINABILITY,
                    )
                  }
                >
                  <Bold>B Corp™</Bold>
                </Link>{' '}
                which means we&apos;ve joined a global community of businesses that are dedicated to
                building a better world. Together we can fight climate change and make the world a
                fairer place for everyone.
                <Space size={2} />
                From recyclable boxes and smart packaging to clever portioning that cuts down on
                waste, we&apos;re all about good food that&apos;s good for the planet, too.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="3" title={renderAccordionHeader('Is Gousto hard to cancel?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Not at all. You can cancel, skip or pause your deliveries at any time through your
                online account or app. No hidden hoops to jump through.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="4" title={renderAccordionHeader(' Can you cancel Gousto after one box?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Yes. Try one box and see how you get on – there’s no long-term commitment. Cancel or
                pause anytime.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="5" title={renderAccordionHeader('Can I hit my health goals with Gousto?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Definitely. Choose from categories like{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={HowGoustoFoodBoxesWorkSections.FAQ_SECTION}
                  target={TargetUri.CALORIE_CONTROLLED}
                  action={TrackingActions.CALORIE_CONTROLLED_HELP}
                  whiteText
                >
                  <Bold>Calorie Controlled,</Bold>
                </LandingPageButton>{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={HowGoustoFoodBoxesWorkSections.FAQ_SECTION}
                  target={TargetUri.PROTEIN_HIT}
                  action={TrackingActions.PROTEIN_HIT_HELP}
                  whiteText
                >
                  <Bold>Protein Hit,</Bold>
                </LandingPageButton>{' '}
                or{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={HowGoustoFoodBoxesWorkSections.FAQ_SECTION}
                  target={TargetUri.VEGETARIAN}
                  action={TrackingActions.VEG_BOOST_HELP}
                  whiteText
                >
                  <Bold>Veg Boost,</Bold>
                </LandingPageButton>{' '}
                to keep your health goals in check without sacrificing flavour. We also have a whole
                range of dishes that are under 600 calories, so you can eat well with less stress.
              </Body2>
            </Box>
          </Accordion>
        </Box>
      </Box>
    </Box>
  </Box>
)
